/* Paris Kaye — design layer behaviour.
   Additive: does not modify any Monaco script. */

(function () {
	'use strict';

	/* ----------------------------------------------------------------------
	   Announcement marquee
	   The announcement-bar section renders a single message. To loop the
	   scroll seamlessly we clone the message until it comfortably exceeds
	   the viewport, then duplicate that whole run once more — the CSS
	   animation translates by -50%, so the second run lands exactly where
	   the first began.
	   ---------------------------------------------------------------------- */

	function buildMarquee(track) {
		if (!track || track.dataset.pkMarquee === 'ready') return;

		var original = track.innerHTML;
		if (!original.trim()) return;

		// Repeat until one run is wider than the viewport, so there is never a gap.
		var runs = 1;
		while (track.scrollWidth < window.innerWidth * 1.5 && runs < 20) {
			track.insertAdjacentHTML('beforeend', original);
			runs++;
		}

		// Duplicate the run so translateX(-50%) is seamless.
		track.innerHTML = track.innerHTML + track.innerHTML;
		track.dataset.pkMarquee = 'ready';
	}

	function initMarquees() {
		document.querySelectorAll('.announcement-bar__text').forEach(buildMarquee);
	}

	/* ----------------------------------------------------------------------
	   Hero image priority
	   Monaco renders banner images with loading="lazy". That is right for
	   banners further down, but the hero is the largest element above the
	   fold — lazy-loading it delays the paint and can flash an empty block.
	   Promote just the first banner to an eager, high-priority fetch.
	   ---------------------------------------------------------------------- */

	function prioritiseHero() {
		var hero = document.querySelector('[id$="__pk_hero"] .banner-with-text img');
		if (!hero) return;
		hero.setAttribute('loading', 'eager');
		hero.setAttribute('fetchpriority', 'high');
		hero.removeAttribute('data-lazy');
	}

	/* ----------------------------------------------------------------------
	   New Arrivals carousel geometry
	   Monaco initialises the carousel with slidesPerView: 3.1 / spaceBetween: 4,
	   so swiper's snap grid is built from ITS widths (~405px) while the design
	   renders 340px cards 22px apart. The mismatch makes each arrow click
	   travel a different distance. Re-point swiper at the real layout.
	   ---------------------------------------------------------------------- */

	/* The design's geometry and motion.

	   Geometry: cards are sized in CSS, so swiper is told to read them
	   ('auto') rather than compute a fractional slidesPerView.

	   Motion: the design's carousel is a native scroll container moved by
	   `track.scrollBy({ left: 724, behavior: 'smooth' })` (landing.js). That
	   724px is exactly two cards plus two gaps — 2 × 340 + 2 × 22 — so the
	   equivalent here is slidesPerGroup: 2, which swiper translates by the
	   same 724px. Monaco ships slidesPerGroup: 1, i.e. half the sweep.

	   `speed` is 534 because that is the MEASURED duration of the design's own
	   724px smooth scroll (sampled frame by frame; Blink scales the duration
	   with distance). mirrorTransitionDuration() below is what makes this
	   value actually reach the screen — see its comment. The fitted easing
	   curve lives in pk-theme.css; see also PK-HANDOVER.md §7. */
	var PK_CAROUSEL_GEOMETRY = {
		slidesPerView: 'auto',
		spaceBetween: 22,
		speed: 534
	};

	/* Cards per movement.

	   Two is the design's desktop sweep. But under touch it means a swipe
	   jumps PAST the neighbouring product — the snap grid has no stop on it,
	   so that card can never be brought to rest and tapped. Small screens
	   therefore advance one card, which is also how the design behaves there:
	   its track is a native scroll container, so a finger can stop anywhere.

	   Applied via swiper's own breakpoints rather than a resize listener:
	   Monaco's breakpoints are min-width 990/1200/1360, so the BASE params
	   govern below 990 and the breakpoint entries take over above it. */
	var PK_GROUP_TOUCH = 1;
	var PK_GROUP_DESKTOP = 2;

	/* Where a slide comes to rest.

	   Desktop shows five cards across a 1440 measure, so the track is
	   left-aligned to the 40px gutter exactly as the design draws it.

	   On a phone only one card fits, and left-aligning it left the card
	   sitting 24px from one edge and 61px from the other — visibly off-centre,
	   and the swipe appeared not to settle anywhere deliberate. Centring the
	   active slide gives equal margins, so each product lands squarely in the
	   middle of the screen. */
	var PK_CENTRED_TOUCH = true;
	var PK_CENTRED_DESKTOP = false;

	/* The design's track is a native scroll container — a mouse cannot drag
	   one, so on mouse-only devices the drag is disabled to match (which also
	   removes swiper's mouse-drag lag entirely). Touch devices keep the
	   swipe: real fingers expect it, and the design scrolls under touch too. */
	if (!('ontouchstart' in window) && !(navigator.maxTouchPoints > 0)) {
		PK_CAROUSEL_GEOMETRY.allowTouchMove = false;
	}

	/* Monaco pins the wrapper's transition-duration with a stylesheet
	   !important (section-product-carousel.css). That doesn't just time the
	   arrow sweep — it animates swiper's intended-INSTANT translate sets too:
	   initial positioning, drag tracking, the post-sweep snap correction.
	   Every micro-adjustment glides and drag lags the pointer by half a
	   second; the carousel reads as struggling. A stylesheet rule cannot
	   yield to swiper's inline 0ms against !important — but an inline
	   !important outranks a stylesheet !important, so swiper's runtime
	   duration is mirrored onto the wrapper inline. Instant sets become
	   instant again and the arrow sweep keeps `speed`. */
	function mirrorTransitionDuration(sw) {
		var wrapper = sw.wrapperEl || (sw.$wrapperEl && sw.$wrapperEl[0]);
		if (!wrapper) return;

		function apply(ms) {
			wrapper.style.setProperty('transition-duration', ms + 'ms', 'important');
		}

		var lastSweepAt = 0;

		sw.on('setTransition', function (a, b) {
			// arg order differs across swiper majors (this bundle is 8.4.3,
			// which passes (swiper, duration)) — dispatch on type instead
			var ms = typeof a === 'number' ? a : b;
			if (typeof ms !== 'number') return;

			/* A zero arriving MID-SWEEP is an interrupter, not the end of the
			   sweep: swiper's resize handler and update() both run
			   slideTo(activeIndex, 0) whenever images/fonts/resize land, and
			   honouring that 0 while animating snaps the track to its target
			   instantly. Two guards, because `animating` alone is not enough:
			   swiper 8 emits slideChange BEFORE setting animating, and
			   Monaco's slideChange handler calls update() in that window —
			   its internal slideTo(..., 0) arrives with animating still
			   false. A legit end-of-sweep zero comes `speed` ms after the
			   sweep's non-zero; anything sooner is an interrupter. */
			if (ms === 0 && sw.animating) return;
			if (ms === 0 && performance.now() - lastSweepAt < 100) return;

			if (ms > 0) lastSweepAt = performance.now();
			apply(ms);
		});

		/* Some drag paths set the wrapper's duration directly rather than
		   through setTransition; pin those moments to instant as well. */
		['touchStart', 'sliderMove', 'transitionEnd'].forEach(function (ev) {
			sw.on(ev, function () { apply(0); });
		});

		apply(0);
	}

	/* Monaco and swiper call update() whenever images, fonts or resizes land.
	   Harmless at rest — but mid-sweep every update() restarts the transition
	   toward a re-measured target (historically the "stutter"; with honest
	   0ms durations it became a hard jump). Park updates while a sweep is
	   running and run the last one when it ends. */
	function deferUpdatesDuringSweep(sw) {
		var pending = false;
		var origUpdate = sw.update.bind(sw);

		var retries = 0;

		function flush() {
			if (!pending) return;
			/* Re-check at fire time — a NEW sweep may have started since the
			   update was parked, and running origUpdate() mid-sweep is the
			   exact interrupter this module exists to prevent. Bounded so a
			   stuck `animating` flag can only defer, never wedge. */
			if (sw.animating && retries < 3) {
				retries++;
				setTimeout(flush, (sw.params.speed || 534) + 300);
				return;
			}
			retries = 0;
			pending = false;
			origUpdate();
		}

		sw.update = function () {
			if (sw.animating) {
				if (!pending) {
					pending = true;
					/* Safety valve: if the sweep's transitionend is ever lost
					   (background tab, editor re-render), don't park the
					   update forever — flush shortly after the sweep should
					   have finished. */
					setTimeout(flush, (sw.params.speed || 534) + 300);
				}
				return;
			}
			origUpdate();
		};

		sw.on('transitionEnd', flush);
	}

	/* Monaco configures swiper with responsive breakpoints —
	   { 990: 2.1, 1200: 3.1, 1360: 4.1 } slidesPerView. Swiper re-applies
	   those from originalParams on every breakpoint pass (init, resize,
	   orientation change), so assigning sw.params.slidesPerView once is not
	   enough: the next pass silently puts Monaco's number back and the track
	   renders with no gaps at all (340px steps instead of 362px).

	   So the breakpoint table itself is rewritten. After this, swiper's own
	   passes re-apply OUR geometry and update() still only needs calling once
	   — repeated updates make the sweep stutter. */
	function applyCarouselGeometry(sw) {
		[sw.params, sw.originalParams].forEach(function (params) {
			if (!params) return;

			/* Base params govern below Monaco's smallest breakpoint (990) —
			   i.e. phones and small tablets, where a swipe must land on every
			   card. */
			Object.assign(params, PK_CAROUSEL_GEOMETRY, {
				slidesPerGroup: PK_GROUP_TOUCH,
				centeredSlides: PK_CENTRED_TOUCH
			});

			/* Breakpoint entries win over the top-level params on every pass,
			   so each one has to carry the same values or it reinstates
			   Monaco's. These are all min-width ≥990, so they carry the
			   desktop two-card sweep. */
			if (!params.breakpoints) return;
			Object.keys(params.breakpoints).forEach(function (bp) {
				Object.assign(params.breakpoints[bp], PK_CAROUSEL_GEOMETRY, {
					slidesPerGroup: PK_GROUP_DESKTOP,
					centeredSlides: PK_CENTRED_DESKTOP
				});
			});
		});

		/* params.slidesPerGroup was just overwritten with the BASE value; if a
		   breakpoint is currently active its value must win. Swiper applies
		   this itself on the next breakpoint pass, but the update() below
		   happens first. */
		var active = sw.currentBreakpoint;
		if (active && sw.params.breakpoints && sw.params.breakpoints[active]) {
			sw.params.slidesPerGroup = sw.params.breakpoints[active].slidesPerGroup;
			sw.params.centeredSlides = sw.params.breakpoints[active].centeredSlides;
		}
	}

	/* Returns true once the carousel is fully healthy: geometry applied AND
	   swiper has actually measured its slides. The two are separate — on some
	   loads (seen on the CDN theme and on background tabs) our update() lands
	   while swiper's slide query still returns 0, leaving an empty snap grid
	   even though 8 cards sit in the DOM. Arrows on that state do nothing or
	   jump erratically. An empty model cannot be mid-transition, so
	   re-updating it is safe and does not hit the update-stutter trap. */
	function syncArrivalsCarousel() {
		var el = document.querySelector('[id$="__pk_new_arrivals"] .swiper-container');
		if (!el || !el.swiper) return false;

		var sw = el.swiper;
		if (sw.destroyed) return false;

		/* Flagged on the instance, not the element: the theme editor destroys
		   and re-creates swiper on the same node, and the replacement needs
		   the geometry applied again. */
		if (!sw.pkGeometryApplied) {
			sw.pkGeometryApplied = true;

			/* The side gutter is a margin on the swiper container (see
			   pk-theme.css): this build ignores slidesOffsetBefore/After, and
			   container padding inflates its clientWidth. */
			applyCarouselGeometry(sw);
			mirrorTransitionDuration(sw);
			deferUpdatesDuringSweep(sw);

			/* allowTouchMove was written into params above, but the live
			   instance copied its own flag from params at init — set it
			   directly too. */
			if (PK_CAROUSEL_GEOMETRY.allowTouchMove === false) {
				sw.allowTouchMove = false;
			}

			sw.update();
			sw.slideTo(sw.activeIndex, 0, false);
		} else if (!(sw.slides && sw.slides.length) || !(sw.snapGrid && sw.snapGrid.length)) {
			/* Geometry applied on an earlier tick but the model is still
			   empty — re-measure until the slides register. */
			sw.update();
			sw.slideTo(sw.activeIndex, 0, false);
		}

		return !!(sw.slides && sw.slides.length && sw.snapGrid && sw.snapGrid.length);
	}

	function initCarouselSync() {
		/* Swiper may initialise well after this script. Hooking a fixed set of
		   moments (fonts.ready, window load) is not enough — on the CDN-served
		   theme swiper lands later than it does on the local dev server, so
		   every hook fired before it existed and the geometry was never
		   applied. Poll until the carousel reports HEALTHY — geometry applied
		   and slides actually measured — not merely until the flag is set;
		   stopping on the flag alone once shipped a dead carousel whose
		   update() had run while swiper still saw zero slides. */
		var tries = 0;

		(function attempt() {
			if (syncArrivalsCarousel()) return;
			if (++tries > 60) return; // give up after ~6s

			setTimeout(attempt, 100);
		})();
	}

	/* ----------------------------------------------------------------------
	   Category tiles — keep the full-tile click target out of the a11y tree.
	   The tile is made clickable by Monaco's nameless overlay anchor (see
	   pk-theme.css §9). An anchor with no text is a WCAG 4.1.2 failure and
	   would also double every tile in the tab order, so it is hidden from
	   assistive tech and skipped by keyboard — the label's link carries the
	   category name and remains the real, focusable link.
	   ---------------------------------------------------------------------- */

	function tidyCategoryOverlayLinks() {
		var overlays = document.querySelectorAll(
			'[id$="__pk_categories"] .collections-grid__card > .card-wrapper__link--overlay'
		);
		Array.prototype.forEach.call(overlays, function (a) {
			a.setAttribute('aria-hidden', 'true');
			a.setAttribute('tabindex', '-1');
		});
	}

	document.addEventListener('shopify:section:load', tidyCategoryOverlayLinks);

	/* ----------------------------------------------------------------------
	   Collection grid — one named link per product card.
	   Monaco hardcodes aria-label="link" on BOTH anchors it puts in a card:
	   the full-card overlay and the one wrapping the title and price. Every
	   card therefore ships two links whose accessible name is the literal
	   word "link" (WCAG 2.4.4 / 4.1.2) and the grid is doubled in the tab
	   order. Same remedy as the category tiles above: the overlay keeps the
	   click target but leaves the a11y tree, and the title anchor drops the
	   bogus label so its own content — product name and price — names it.
	   ---------------------------------------------------------------------- */

	function tidyProductCardLinks(root) {
		var cards = (root || document).querySelectorAll('.collection-product-card');
		Array.prototype.forEach.call(cards, function (card) {
			var overlay = card.querySelector('.card-wrapper__link--overlay');
			if (overlay && overlay.getAttribute('aria-hidden') !== 'true') {
				overlay.setAttribute('aria-hidden', 'true');
				overlay.setAttribute('tabindex', '-1');
			}
			var named = card.querySelector('.card-information a.full-unstyled-link');
			if (named && named.getAttribute('aria-label') === 'link') {
				named.removeAttribute('aria-label');
			}
		});
	}

	/* ----------------------------------------------------------------------
	   Applied-filter bar — one node for the life of the page.

	   The bar is rendered OUTSIDE #ProductGridContainer precisely so that
	   facets.js cannot destroy it (it replaces that container's innerHTML
	   wholesale on every filter change, and a rebuilt node can only ever
	   animate in, never out). The chips themselves still have to come from
	   Liquid — `value.url_to_remove` / `filter.url_to_remove` are Shopify's
	   own removal URLs and must not be reconstructed here — so the section
	   renders a hidden copy of them INSIDE the container, and this reads it
	   across after each render. See §16c and the note in
	   sections/main-collection-product-grid.liquid.
	   ---------------------------------------------------------------------- */

	function syncAppliedBar() {
		var bar = document.querySelector('.pk-applied');
		var source = document.querySelector(
			'#ProductGridContainer .pk-applied-source'
		);
		if (!bar || !source) return;

		var inner = bar.querySelector('.pk-applied__inner');
		var active = source.children.length > 0;

		/* Chips are replaced on the way IN only. The design deliberately keeps
		   the last set rendered while the bar collapses — empty it mid-collapse
		   and "Clear All" snaps leftwards across the row while the box is still
		   visibly closing. They are cleared by the next set that arrives. */
		if (active && inner.innerHTML !== source.innerHTML) {
			inner.innerHTML = source.innerHTML;
		}

		bar.classList.toggle('is-active', active);

		/* Those held-over chips are real links in a zero-height box, so the
		   closed bar has to leave the tab order and the a11y tree — which the
		   old destroy-on-render bar got for free by not existing. */
		if (active) bar.removeAttribute('inert');
		else bar.setAttribute('inert', '');
	}

	/* The grid is rebuilt underneath us twice over: facets.js replaces the
	   whole of #ProductGridContainer on every filter or sort change, and
	   load-more.js appends fresh cards as the sentinel scrolls into view.
	   Observing childList only — the attributes written above cannot
	   re-trigger it, and syncAppliedBar() writes to the bar, which sits
	   outside the observed container, so it cannot re-trigger it either. */
	function watchProductGrid() {
		var container = document.getElementById('ProductGridContainer');
		if (!container) return;
		tidyProductCardLinks(container);
		syncAppliedBar();
		new MutationObserver(function () {
			tidyProductCardLinks(container);
			syncAppliedBar();
		}).observe(container, { childList: true, subtree: true });
	}

	document.addEventListener('shopify:section:load', watchProductGrid);

	/* ----------------------------------------------------------------------
	   Filter drawer — the design's header reads "Filter (2)" while filters
	   are applied. Monaco's string is just "Filter" (the count it is handed
	   goes unused), so the tally is appended here.

	   Checkbox facets are counted from the DOM, so the number moves the
	   instant one is ticked exactly as it does in the design — facets.js only
	   re-renders after a 500ms debounce. The price range has no checked state
	   to read, so it comes from the URL, which facets.js keeps current.
	   ---------------------------------------------------------------------- */

	function syncFilterCount() {
		var title = document.querySelector('.facets-menu__title');
		if (!title) return;
		if (!title.dataset.pkLabel) title.dataset.pkLabel = title.textContent.trim();

		var n = document.querySelectorAll(
			'.facets-menu .facets-checkbox__hide:checked'
		).length;

		/* A price range counts only once it actually narrows something.
		   Monaco's facet form serialises the price inputs on every submit, so
		   sorting alone puts `filter.v.price.gte=0&lte=<max>` in the URL —
		   reading the URL would badge "Filter (2)" with nothing filtered.
		   The inputs carry the bounds, so they answer it directly, and they
		   answer it live rather than a request behind. */
		var lo = document.querySelector('.facets-menu [name$="price.gte"]');
		var hi = document.querySelector('.facets-menu [name$="price.lte"]');
		if (lo && hi) {
			var loV = parseFloat(lo.value);
			var hiV = parseFloat(hi.value);
			var hiMax = parseFloat(hi.getAttribute('max'));
			if ((loV > 0) || (!isNaN(hiMax) && hiV < hiMax)) n += 1;
		}

		var next = n ? title.dataset.pkLabel + ' (' + n + ')' : title.dataset.pkLabel;
		if (title.textContent.trim() !== next) title.textContent = next;
	}

	function initFilterDrawer() {
		var menu = document.querySelector('.facets-menu');
		if (!menu) return;

		/* Monaco's close control is an anchor wrapping nothing but an icon, so
		   it ships with no accessible name — and pk-theme.css swaps that icon
		   for the design's ✕ glyph, which would leave it with none at all. */
		var close = menu.querySelector('.facets-menu__close');
		if (close && !close.getAttribute('aria-label')) {
			close.setAttribute('aria-label', 'Close filters');
		}

		syncFilterCount();

		document.addEventListener('change', function (e) {
			if (e.target && e.target.closest && e.target.closest('.facets-menu')) {
				syncFilterCount();
			}
		});

		/* facets.js swaps out each <details> on every filter change. Observing
		   the list rather than the whole drawer keeps the title — which this
		   writes to — outside the observed subtree, so it cannot loop. */
		var list = menu.querySelector('.facets__details__wrapper');
		if (list) {
			new MutationObserver(syncFilterCount).observe(list, {
				childList: true,
				subtree: true
			});
		}
	}

	document.addEventListener('shopify:section:load', initFilterDrawer);

	/* ----------------------------------------------------------------------
	   Sort — the design's dropdown, built over Monaco's <select>.

	   The design draws a cream panel with a tick against the active option;
	   a native <select> can only ever show the OS list. So the native control
	   is kept as the FORM FIELD — facets.js reads it out of the form's
	   FormData and re-renders on the form's `input` event — but is hidden,
	   untabbable and aria-hidden, and this menu drives it. That keeps
	   Monaco's AJAX sort, the URL handling and the no-JS fallback intact
	   while the visible control is the design's.

	   Exactly one sort control is exposed to assistive tech, and it is this
	   one: a button + listbox with arrow-key navigation and Escape to close.
	   ---------------------------------------------------------------------- */

	function initSortMenu() {
		if (!document.body.classList.contains('template-collection')) return;

		var group = document.querySelector('.facets__sort-by');
		if (!group || group.querySelector('.pk-sort')) return;
		var select = group.querySelector('.select__sort_by');
		if (!select || !select.options.length) return;

		var wrap = document.createElement('div');
		wrap.className = 'pk-sort';

		var trigger = document.createElement('button');
		trigger.type = 'button';
		trigger.className = 'pk-sort__trigger';
		trigger.setAttribute('aria-haspopup', 'listbox');
		trigger.setAttribute('aria-expanded', 'false');

		var labelEl = document.createElement('span');
		labelEl.className = 'pk-sort__label';

		var caret = document.createElement('span');
		caret.className = 'pk-sort__caret';
		caret.setAttribute('aria-hidden', 'true');
		caret.textContent = '▾';

		trigger.appendChild(labelEl);
		trigger.appendChild(caret);

		var panel = document.createElement('div');
		panel.className = 'pk-sort__panel';
		panel.setAttribute('role', 'listbox');

		var options = [];
		Array.prototype.forEach.call(select.options, function (opt) {
			var b = document.createElement('button');
			b.type = 'button';
			b.className = 'pk-sort__option';
			b.setAttribute('role', 'option');
			b.setAttribute('data-value', opt.value);

			var name = document.createElement('span');
			name.textContent = opt.textContent.trim();
			var tick = document.createElement('span');
			tick.className = 'pk-sort__tick';
			tick.setAttribute('aria-hidden', 'true');
			tick.textContent = '✓';

			b.appendChild(name);
			b.appendChild(tick);
			panel.appendChild(b);
			options.push(b);
		});

		wrap.appendChild(trigger);
		wrap.appendChild(panel);
		group.appendChild(wrap);

		select.setAttribute('tabindex', '-1');
		select.setAttribute('aria-hidden', 'true');

		function paint() {
			var current = select.options[select.selectedIndex];
			var text = current ? current.textContent.trim() : '';
			labelEl.textContent = text;
			trigger.setAttribute('aria-label', 'Sort by: ' + text);
			options.forEach(function (b) {
				var on = b.getAttribute('data-value') === select.value;
				b.classList.toggle('is-selected', on);
				b.setAttribute('aria-selected', on ? 'true' : 'false');
			});
		}

		function isOpen() {
			return wrap.classList.contains('is-open');
		}

		function setOpen(open) {
			wrap.classList.toggle('is-open', open);
			trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
		}

		trigger.addEventListener('click', function (e) {
			e.stopPropagation();
			setOpen(!isOpen());
		});

		options.forEach(function (b) {
			b.addEventListener('click', function (e) {
				e.stopPropagation();
				if (select.value !== b.getAttribute('data-value')) {
					select.value = b.getAttribute('data-value');
					paint();
					/* facets.js listens for `input` on the form (debounced). */
					select.dispatchEvent(new Event('input', { bubbles: true }));
				}
				setOpen(false);
				trigger.focus();
			});
		});

		/* Clicking anywhere else closes it, as in the design. */
		document.addEventListener('click', function () {
			if (isOpen()) setOpen(false);
		});
		panel.addEventListener('click', function (e) {
			e.stopPropagation();
		});

		trigger.addEventListener('keydown', function (e) {
			if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				setOpen(true);
				(panel.querySelector('.is-selected') || options[0]).focus();
			} else if (e.key === 'Escape') {
				setOpen(false);
			}
		});

		panel.addEventListener('keydown', function (e) {
			var i = options.indexOf(document.activeElement);
			if (e.key === 'Escape') {
				e.preventDefault();
				setOpen(false);
				trigger.focus();
			} else if (e.key === 'ArrowDown') {
				e.preventDefault();
				options[Math.min(options.length - 1, i + 1)].focus();
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				options[Math.max(0, i - 1)].focus();
			}
		});

		paint();
	}

	document.addEventListener('shopify:section:load', initSortMenu);

	/* ----------------------------------------------------------------------
	   Product page — sticky add-to-cart bar.
	   Not a Monaco component. It mirrors the page's own product form rather
	   than posting to the cart itself: the button calls requestSubmit() on
	   `form[id^="product-form-"]`, so Shopify's add-to-cart path, its error
	   handling and the cart-drawer hand-off are all untouched.
	   ---------------------------------------------------------------------- */

	function currentVariantLabel() {
		var picked = [];
		var groups = document.querySelectorAll('.product-form__input--radios');
		Array.prototype.forEach.call(groups, function (g) {
			var on = g.querySelector('input[type="radio"]:checked');
			if (!on) return;
			var v = on.value;
			/* A product with no real options still ships one radio called
			   "Default Title" — that is Shopify's placeholder, not a choice. */
			if (v && v !== 'Default Title') picked.push(v);
		});
		return picked.join(' • ');
	}

	function initStickyAtc() {
		if (!document.body.classList.contains('template-product')) return;
		if (document.querySelector('.pk-atc')) return;

		var form = document.querySelector('form[id^="product-form-"]');
		var title = document.querySelector('.product__title');
		var buyArea = document.querySelector('product-form');
		if (!form || !title || !buyArea) return;

		var bar = document.createElement('div');
		bar.className = 'pk-atc';

		var inner = document.createElement('div');
		inner.className = 'pk-atc__inner';

		var left = document.createElement('div');
		left.className = 'pk-atc__left';
		var t = document.createElement('div');
		t.className = 'pk-atc__title';
		t.textContent = title.textContent.trim();
		var v = document.createElement('div');
		v.className = 'pk-atc__variant';
		v.textContent = currentVariantLabel();
		left.appendChild(t);
		left.appendChild(v);

		var right = document.createElement('div');
		right.className = 'pk-atc__right';
		var p = document.createElement('div');
		p.className = 'pk-atc__price';
		var priceEl = document.querySelector('.price-wrapper .price-item');
		p.textContent = priceEl ? priceEl.textContent.trim() : '';
		var btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'pk-atc__button';
		btn.textContent = 'Add to Cart';
		btn.addEventListener('click', function () {
			if (form.requestSubmit) form.requestSubmit();
			else form.submit();
		});
		right.appendChild(p);
		right.appendChild(btn);

		inner.appendChild(left);
		inner.appendChild(right);
		bar.appendChild(inner);
		document.body.appendChild(bar);

		/* Keep the mirrored variant and price current as options change. */
		document.addEventListener('change', function (e) {
			if (e.target && e.target.closest && e.target.closest('.product-form__input')) {
				v.textContent = currentVariantLabel();
				var pe = document.querySelector('.price-wrapper .price-item');
				if (pe) p.textContent = pe.textContent.trim();
			}
		});

		/* The bar appears once the real buy area has scrolled off the top —
		   the design's own rule, so it never covers the button it mirrors. */
		var cartOpen = function () {
			var cd = document.querySelector('cart-drawer');
			return !!(cd && cd.classList.contains('active'));
		};
		var visible = false;
		var paint = function () {
			bar.classList.toggle('is-on', visible && !cartOpen());
		};
		new IntersectionObserver(
			function (entries) {
				var e = entries[0];
				visible = !e.isIntersecting && e.boundingClientRect.top < 0;
				paint();
			},
			{ threshold: 0 }
		).observe(buyArea);

		/* Hide it while the cart drawer is open, as the design does. */
		var cd = document.querySelector('cart-drawer');
		if (cd) new MutationObserver(paint).observe(cd, { attributes: true, attributeFilter: ['class'] });
	}

	/* ----------------------------------------------------------------------
	   Product page — prev/next for the recommendations row.
	   Monaco stacks the recommendations and offers an "Explore" link; the
	   design runs them as a horizontal scroller with two arrows. The row is
	   fetched lazily, so this also watches for it arriving.
	   ---------------------------------------------------------------------- */

	function buildRecsArrows() {
		var header = document.querySelector('.product-recommendations__header--container');
		var list = document.querySelector('.product-recommendations__list');
		if (!header || !list || header.querySelector('.pk-recs-nav')) return;

		var nav = document.createElement('div');
		nav.className = 'pk-recs-nav';
		[['Previous', '←', -1], ['Next', '→', 1]].forEach(function (spec) {
			var b = document.createElement('button');
			b.type = 'button';
			b.setAttribute('aria-label', spec[0]);
			b.textContent = spec[1];
			b.addEventListener('click', function () {
				list.scrollBy({ left: spec[2] * 700, behavior: 'smooth' });
			});
			nav.appendChild(b);
		});
		header.appendChild(nav);
	}

	function initRecsArrows() {
		if (!document.body.classList.contains('template-product')) return;
		var host = document.querySelector('product-recommendations-also-like');
		if (!host) return;
		buildRecsArrows();
		new MutationObserver(buildRecsArrows).observe(host, {
			childList: true,
			subtree: true
		});
	}

	/* Accordion panels are revealed with a max-height transition (§17d), which
	   needs a NUMBER — `auto` cannot be transitioned. A single hardcoded cap
	   cannot serve both a 69px panel and a 381px one: too small clips the copy
	   (11 of the 161 products carry descriptions taller than the old 220px cap,
	   the worst losing 161px mid-sentence), and too large ruins the motion,
	   because the panel finishes opening as soon as max-height passes the
	   content height — with a 600px cap a 94px panel is fully open ~6% into the
	   curve, i.e. it pops. So each panel is measured and capped at its own
	   height.

	   `scrollHeight` is safe to read while the panel is shut: it reports the
	   full content height regardless of the max-height clipping. */
	function sizeAccordionPanels() {
		if (!document.body.classList.contains('template-product')) return;
		var panels = document.querySelectorAll('.about__accordion-description');
		for (var i = 0; i < panels.length; i++) {
			// Clear first, or a panel that has grown re-measures against its own
			// stale cap and can only ever shrink.
			panels[i].style.removeProperty('--pk-panel-h');
			panels[i].style.setProperty('--pk-panel-h', panels[i].scrollHeight + 'px');
		}
	}

	function initAccordionPanels() {
		if (!document.body.classList.contains('template-product')) return;
		sizeAccordionPanels();
		// Text reflows when the webfont swaps in and on every resize, and the
		// measurement is only correct for the width it was taken at.
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(sizeAccordionPanels);
		}
		var t;
		window.addEventListener('resize', function () {
			clearTimeout(t);
			t = setTimeout(sizeAccordionPanels, 150);
		});
	}

	/* §21 — header search panel.

	   Two behaviours the design specifies that Monaco has no equivalent for:
	   clicking a popular term searches for it in place, and the two column
	   labels change once there is a query ("Popular Searches" → "Suggestions",
	   "Trending Now" → Results for "…").

	   The terms are real links to the results page, so this is an upgrade, not
	   the mechanism — with JS off they still work, they just navigate. */
	function initHeaderSearch() {
		var root = document.querySelector('.header__search');
		if (!root || root.dataset.pkSearchBound) return;
		root.dataset.pkSearchBound = '1';

		var input = root.querySelector('.search__input');
		var termsLabel = root.querySelector('[data-pk-label-terms]');
		var resultsLabel = root.querySelector('[data-pk-label-results]');
		if (!input) return;

		var strings = {
			popular: termsLabel ? termsLabel.textContent.trim() : '',
			trending: resultsLabel ? resultsLabel.textContent.trim() : '',
			suggestions: root.dataset.pkSuggestions || 'Suggestions',
			results: root.dataset.pkResults || 'Results for “%q%”'
		};

		function paintLabels() {
			var q = input.value.trim();
			if (termsLabel) {
				termsLabel.textContent = q ? strings.suggestions : strings.popular;
			}
			if (resultsLabel) {
				resultsLabel.textContent = q
					? strings.results.replace('%q%', q)
					: strings.trending;
			}
		}

		/* How much room the panel actually has. A CSS `calc(100vh - header)`
		   cannot know this: the panel hangs off the STICKY header, so its top
		   is the header height once scrolled but header + announcement bar at
		   scroll 0 — measured 228px at 375, where the guess was 75. Anything
		   past that ran off the bottom of the screen with no way to reach it.
		   Measured on open, on resize and on scroll instead. */
		var content = root.querySelector('.search-modal__content');
		var wrapper = root.querySelector('.search-modal');

		function sizePanel() {
			if (!content || !wrapper) return;
			var top = wrapper.getBoundingClientRect().top;
			content.style.setProperty('--pk-search-max', Math.max(160, window.innerHeight - top - 24) + 'px');
		}

		root.addEventListener('toggle', sizePanel, true);
		window.addEventListener('resize', sizePanel);
		window.addEventListener('scroll', sizePanel, { passive: true });
		sizePanel();

		input.addEventListener('input', paintLabels);

		root.addEventListener('click', function (e) {
			var term = e.target.closest('[data-pk-term]');
			if (!term) return;
			// Search in place rather than navigating, as the design does.
			e.preventDefault();
			input.value = term.textContent.trim();
			input.focus();
			// `input` is the event predictive-search.js listens to; `change`
			// keeps the plain (non-predictive) form in step.
			input.dispatchEvent(new Event('input', { bubbles: true }));
			input.dispatchEvent(new Event('change', { bubbles: true }));
			paintLabels();
		});

		/* PK (client, 2026-08-12): the panel IS the search — there is to be no
		   separate results page. Everything in this form navigates to /search
		   on submit: Enter (implicit submission), Monaco's hidden no-JS submit
		   and the "View All Results" control. Cancelling at the form catches all
		   three in one place.

		   Safe against predictive search: it fetches on `input`, never on
		   submit, and its own `onFormSubmit` only ever ADDS a preventDefault
		   (for a selected option or a card link). Clicking a result is a plain
		   <a href> and is not a submission, so results stay reachable. The
		   popular terms already search in place, just above. */
		var form = root.querySelector('form');
		if (form) {
			form.addEventListener('submit', function (e) {
				e.preventDefault();
				input.focus();
			});
		}

		paintLabels();
	}

	/* §18 — cart drawer: the gift note and the "pairs well with" Add buttons.

	   Both are bound on `document`, not on the elements. CartDrawerItems
	   re-renders `.drawer__inner` — the WHOLE drawer — from the server on every
	   quantity change and removal, so anything bound directly is destroyed the
	   first time the shopper changes a quantity. Same lesson as trap 22. */
	function initCartDrawerExtras() {
		if (window.pkCartExtrasBound) return;
		window.pkCartExtrasBound = true;

		var noteTimer;

		/* Go through `window.routes`, not a hardcoded `/cart/...`: those routes
		   carry the locale prefix on multi-language stores. `.js` is appended
		   defensively — Shopify emits them without it, but a double `.js.js`
		   would 404 silently. */
		function ajaxRoute(name, fallback) {
			var base = (window.routes && window.routes[name]) || fallback;
			return base.replace(/\.js$/, '') + '.js';
		}

		function saveNote(value) {
			clearTimeout(noteTimer);
			noteTimer = setTimeout(function () {
				fetch(ajaxRoute('cart_update_url', '/cart/update'), {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ note: value })
				});
			}, 400);
		}

		document.addEventListener('input', function (e) {
			var ta = e.target.closest ? e.target.closest('.pk-cart-note__input') : null;
			if (ta) saveNote(ta.value);
		});

		/* ⚠ Monaco's CartItems binds `change` on <cart-drawer-items> ITSELF and
		   reads `event.target.dataset.index` as the line to update (cart.js).
		   So ANY control we add inside that element fires a phantom quantity
		   update: the gift-note checkbox and textarea both do, and neither
		   carries an index — /cart/change.js is POSTed `line: undefined,
		   quantity: "on"`, its reply has no `items` array, cart.js throws
		   reading `parsedState.items[line - 1]` and its own .catch paints
		   "There was an error while updating your cart."

		   Contained in the CAPTURE phase on `document`, which is the only place
		   that runs BEFORE cart-items sees the event. `input` needs no such
		   guard — cart.js listens to `change` only.

		   ⚠ Capture-phase stopPropagation ends the event's journey outright: it
		   never reaches the target and therefore never bubbles back to
		   document, so the untick handling below has to live in THIS listener
		   and not in a second, bubbling one. Anything of ours that goes inside
		   the drawer and fires `change` belongs in this selector. */
		document.addEventListener('change', function (e) {
			var t = e.target;
			if (!t || !t.closest || !t.closest('.pk-cart-note')) return;
			if (t.closest('cart-items, cart-drawer-items')) e.stopPropagation();

			/* Unticking the box is an instruction to drop the note, not just to
			   hide the field — otherwise a note the shopper has visibly
			   dismissed still arrives on the order. */
			if (!t.classList.contains('pk-cart-note__check') || t.checked) return;
			var wrap = t.closest('.pk-cart-note');
			var ta = wrap && wrap.querySelector('.pk-cart-note__input');
			if (ta) ta.value = '';
			saveNote('');
		}, true);

		document.addEventListener('click', function (e) {
			var btn = e.target.closest ? e.target.closest('[data-pk-addon-id]') : null;
			if (!btn || btn.disabled) return;
			e.preventDefault();
			btn.disabled = true;
			/* `sections` asks Shopify to return the re-rendered drawer in the
			   same round trip, so the new line, the subtotal and the shipping
			   bar all land together instead of in three states. */
			fetch(ajaxRoute('cart_add_url', '/cart/add'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({
					items: [{ id: Number(btn.dataset.pkAddonId), quantity: 1 }],
					sections: 'cart-drawer'
				})
			})
				.then(function (r) { return r.json(); })
				.then(function (data) {
					var html = data && data.sections && data.sections['cart-drawer'];
					var inner = document.querySelector('cart-drawer .drawer__inner');
					if (!html || !inner) { btn.disabled = false; return; }
					var fresh = new DOMParser()
						.parseFromString(html, 'text/html')
						.querySelector('.drawer__inner');
					if (fresh) inner.replaceWith(fresh);
					var bubble = document.querySelector('#cart-icon-bubble');
					if (bubble) bubble.classList.add('pk-cart-updated');
				})
				.catch(function () { btn.disabled = false; });
		});
	}

	/* §22 — About page scroll reveals.

	   This only ever ADDS `.pk-play` to run a one-shot animation — no `else`
	   branch, no cleanup that removes the class — because if it never runs the
	   page has to degrade to "no animation" rather than to invisible text.

	   ⚠ The resting state that makes the animation start from its own frame 0
	   lives in CSS (`pk-reveal-armed`, §22a) and is switched on by an inline
	   script in `pk-about.liquid`, NOT here: by the time a `defer` script runs,
	   the hero has usually been painted, and arming it then just relocates the
	   flash to page load. What this function owes that arrangement is the
	   `data-pk-reveal-live` claim below.

	   The design's own handler fires when an element's top passes 90% of the
	   viewport; `rootMargin: 0px 0px -10%` is the observer equivalent, and it
	   also fires immediately for anything already on screen at load. */
	function initAboutReveals() {
		var root = document.querySelector('.pk-about');
		if (!root) return;

		var els = root.querySelectorAll('.pk-reveal:not(.pk-play)');
		if (!els.length) return;

		/* Claim the armed state the inline script set at parse time. Without
		   this attribute that script un-arms again at DOMContentLoaded, so a
		   pk-theme.js that 404s or throws before this point cannot strand the
		   page invisible.

		   ⚠ The `add` is not redundant with the inline script: on a theme-editor
		   `shopify:section:load` the section is re-injected as innerHTML, and
		   scripts inserted that way never execute — so this is the only thing
		   that re-arms in the editor. */
		root.setAttribute('data-pk-reveal-live', '');
		root.classList.add('pk-reveal-armed');

		if (!('IntersectionObserver' in window)) {
			for (var i = 0; i < els.length; i++) els[i].classList.add('pk-play');
			return;
		}

		var io = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) return;
				entry.target.classList.add('pk-play');
				io.unobserve(entry.target);
			});
		}, { rootMargin: '0px 0px -10% 0px' });

		els.forEach(function (el) { io.observe(el); });
	}

	/* §22 — the studio video starts itself when the visitor reaches it, runs
	   once, and holds its last frame.

	   ⚠ Not the `autoplay` attribute: that fires on page load, and this band is
	   more than a screen down, so the video would have finished before anyone
	   saw it. An IntersectionObserver at 35% is what "when someone reaches it"
	   actually means.

	   ⚠ `muted` is re-asserted here as well as in Liquid. An unattended `play()`
	   is refused by every browser on an unmuted video, and the returned promise
	   rejects — so it is also caught, or a blocked play throws into the console
	   on every load.

	   Observed once and then unobserved: the client wants it to finish and stay
	   finished, so scrolling back must not restart it. */
	function initAboutVideos() {
		var vids = document.querySelectorAll('.pk-about-story__video');
		if (!vids.length) return;

		/* ⚠ The clip ENDS ON A BLACK FRAME, so `loop: false` alone gives a black
		   panel, not a freeze — the client reported exactly that. Sampled off a
		   canvas: the picture is still there at 12.02s (avg brightness 81) and
		   pure black by 12.10s. "Freeze on the last shot" therefore means the
		   last frame that still HAS a shot in it, not the literal final frame.

		   Done on `ended` rather than by polling `timeupdate`: that event only
		   fires about every 250ms, so it would routinely overshoot a tail this
		   short and land on the black anyway. Seeking after `ended` repaints,
		   clears `ended`, and leaves the video paused on the frame — which is
		   the freeze that was asked for.

		   ⚠ If a future clip fades out over a second or more, this 0.15s will
		   not be enough and it will still settle on near-black. Re-trim the
		   video or raise this. */
		var HOLD_BACK = 0.15;

		function holdLastFrame(v) {
			if (v.dataset.pkHold === '1') return;
			v.dataset.pkHold = '1';
			v.addEventListener('ended', function () {
				var t = v.duration - HOLD_BACK;
				if (isFinite(t) && t > 0) v.currentTime = t;
			});
		}

		function start(v) {
			v.muted = true;
			var p = v.play();
			if (p && p.catch) p.catch(function () {});
		}

		for (var n = 0; n < vids.length; n++) holdLastFrame(vids[n]);

		if (!('IntersectionObserver' in window)) {
			for (var i = 0; i < vids.length; i++) start(vids[i]);
			return;
		}

		var io = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) return;
				io.unobserve(entry.target);
				start(entry.target);
			});
		}, { threshold: 0.35 });

		vids.forEach(function (v) { io.observe(v); });
	}

	/* The marquee duration is a section setting, so it arrives as a data
	   attribute rather than in the stylesheet. */
	function initAboutMarquee() {
		var track = document.querySelector('.pk-about-marquee__track[data-pk-duration]');
		if (!track) return;
		var dur = parseInt(track.dataset.pkDuration, 10);
		if (dur > 0) track.style.setProperty('--pk-about-marquee-dur', dur + 's');
	}

	document.addEventListener('shopify:section:load', initStickyAtc);
	document.addEventListener('shopify:section:load', initRecsArrows);
	document.addEventListener('shopify:section:load', sizeAccordionPanels);
	document.addEventListener('shopify:section:load', initHeaderSearch);
	document.addEventListener('shopify:section:load', initAboutReveals);
	document.addEventListener('shopify:section:load', initAboutMarquee);
	document.addEventListener('shopify:section:load', initAboutVideos);

	document.addEventListener('shopify:section:load', initCarouselSync);

	prioritiseHero();
	initCarouselSync();
	tidyCategoryOverlayLinks();
	watchProductGrid();
	initFilterDrawer();
	initSortMenu();
	initStickyAtc();
	initRecsArrows();
	initAccordionPanels();
	initHeaderSearch();
	initCartDrawerExtras();
	initAboutReveals();
	initAboutMarquee();
	initAboutVideos();

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initMarquees);
	} else {
		initMarquees();
	}

	// The theme editor re-renders sections; rebuild when that happens.
	document.addEventListener('shopify:section:load', initMarquees);
})();
